import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faRemove, faUpload } from "@fortawesome/free-solid-svg-icons";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../../core/utils";
import { FileInputType } from "../../../constants/enums";
import { AttachFile, ParameterCatalog } from "../../../models";
import { FilesApiService, LanguageService } from "../../../services";
import { PictureComponent } from "../../general/picture/picture";

@Component({
  selector: "adaa-form-input-file",
  imports: [ReactiveFormsModule, FontAwesomeModule, TranslatePipe, PictureComponent],
  templateUrl: "./form-input-file.html",
  styleUrl: "./form-input-file.scss",
})
export class FormInputFileComponent {
  private readonly _filesApiService = inject(FilesApiService);
  private readonly _translateService = inject(TranslateService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _iconLibrary = inject(FaIconLibrary);
  private readonly _requiredFromValidator = signal<boolean>(false);
  readonly languageService = inject(LanguageService);

  readonly AdaaHelper = AdaaHelper;
  uploadSuccess: boolean = false;
  uploadError: boolean = false;
  isLoading: boolean = false;
  fileName = signal<string>("");
  fileSize = signal<number>(0);
  _fileData: AttachFile = { id: -1, filename: "" };

  readonly controlName = input<string | null>(null);
  readonly required = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly file = input<AttachFile>({ id: -1, filename: "" });
  readonly allowedFileSize = input<number | undefined>(undefined);
  readonly allowedFilesMessage = input<string>("");
  readonly dropFileZoneMessage = input<string>("common.form.label.drop_file");
  readonly dropFileZoneDisabledMessage = input<string>("common.tables.no_info");
  readonly onDeleteRemoveFile = input<boolean>(true);
  readonly uploadOnSelect = input<boolean>(true);
  readonly attachUrl = input<string>();
  readonly downloadUrl = input<string>();
  readonly allowedFiles = input<FileInputType[]>([
    FileInputType.txt,
    FileInputType.pdf,
    FileInputType.doc,
    FileInputType.docx,
  ]);
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  readonly inputChanges = output<AttachFile | null>();
  readonly fileSelected = output<File | null>();

  private _fileInput = viewChild<ElementRef>("fileInput");

  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());

  fileIcon = computed(() => {
    const filename = this.fileName();
    const $ext = filename.substring(filename.lastIndexOf("."));

    if (this.showContent) {
      return "fa-solid fa-photo-film";
    }

    switch ($ext) {
      case FileInputType.doc:
      case FileInputType.docx: {
        return "fa-solid fa-file-word";
      }
      case FileInputType.ppt:
      case FileInputType.pptx: {
        return "fa-solid fa-file-powerpoint";
      }
      case FileInputType.csv: {
        return "fa-solid fa-file-csv";
      }
      case FileInputType.xls:
      case FileInputType.xlsx: {
        return "fa-solid fa-file-excel";
      }
      case FileInputType.zip: {
        return "fa-solid fa-file-zipper";
      }
      case FileInputType.pdf: {
        return "fa-solid fa-file-pdf";
      }
      case FileInputType.txt: {
        return "fa-solid fa-file-lines";
      }
      default: {
        return "fa-solid fa-file";
      }
    }
  });

  constructor() {
    this._iconLibrary.addIcons(faUpload);
    this._iconLibrary.addIcons(faRemove);

    effect(() => {
      const f = this.file();
      untracked(() => {
        this._fileData = { ...f };
        this.fileName.set(f.filename ?? "");
      });
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          untracked(() => this._requiredFromValidator.set(field.mandatory === "Y"));
        }
      }
    });
  }

  public get showContent(): boolean {
    const images: FileInputType[] = [
      FileInputType.bmp,
      FileInputType.gif,
      FileInputType.jpeg,
      FileInputType.jpeg,
      FileInputType.jpg,
      FileInputType.png,
      FileInputType.svg,
    ];
    const extension = this.fileName().substring(this.fileName().lastIndexOf("."));
    return images.findIndex((e) => e === extension.toLowerCase()) > -1;
  }

  public get allowedFileTypes(): string {
    return this.allowedFiles()
      .map((e) => e.replace(".", "").toLocaleUpperCase())
      .join(", ");
  }

  public downloadFile() {
    if (this._fileData?.id && this._fileData?.filename) {
      this._filesApiService.save(this._fileData.id, this._fileData.filename, false, this.downloadUrl());
    }
  }

  public onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files as FileList;
    const file = files[0] as File | null;
    this._uploadFile(file);
  }

  public onFileDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0] as File | null;
    this._uploadFile(file);
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  public removeFile(): void {
    this.fileName.set("");

    this._fileInput()!.nativeElement.value = null;

    if (!this.uploadOnSelect()) {
      this.fileSelected.emit(null);
      return;
    }

    this.inputChanges.emit(null);

    if (this.onDeleteRemoveFile())
      this._filesApiService.deleteFile(this._fileData.id).subscribe({
        next: () => {
          this.fileSize.set(0);
          this.uploadSuccess = false;
          this.uploadError = false;
        },
      });
  }

  private _checkFileType() {
    const extension = this.fileName().substring(this.fileName().lastIndexOf("."));

    if (this.allowedFiles().find((e) => e === extension.toLowerCase())) {
      return true;
    } else {
      this._toastrService.error(this._translateService.instant("notification.error.file_type_invalid"));
      return false;
    }
  }

  private _uploadFile(file: File | null): void {
    if (file) {
      this.fileName.set(file.name);

      if (!this._checkFileType()) {
        this.fileName.set("");
        return;
      }

      if (this.allowedFileSize() && file.size > this.allowedFileSize()!) {
        this._toastrService.warning(this._translateService.instant("notification.warning.fileSize"));
        return;
      }

      this.fileSize.set(Math.round(file.size / 1024));

      if (!this.uploadOnSelect()) {
        this.fileSelected.emit(file);
        return;
      }

      this.isLoading = true;
      this._filesApiService.upload(file, false, this.attachUrl()).subscribe({
        next: (response) => {
          this._fileData.id = response.responseData as number;
          this._fileData.filename = file.name;
          this.uploadSuccess = true;
          this.uploadError = false;
          this.isLoading = false;
          this.inputChanges.emit(this._fileData);
        },
      });
    } else {
      this.uploadSuccess = false;
      this.uploadError = true;
      this.isLoading = false;
      this.fileName.set("");
    }
  }
}
